/**
 * Sign in with Apple, loaded from
 * https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js
 *
 * Popup mode, not redirect mode. A popup resolves a promise in this page, so
 * the sign-in screen keeps its wizard step and its redirect target, one code
 * path serves all three origins, and a fan closing the window is a legible
 * rejection rather than a navigation to read query parameters from.
 *
 * `redirectURI` is required even in popup mode: Apple validates it against the
 * Service ID's registered Return URLs before it opens anything. Apple refuses
 * `http://localhost`, so this flow cannot be completed on a local dev server.
 */

import { loadExternalScript } from "./scriptLoader";

type typeAppleSignInResponse = {
  authorization?: { id_token?: string };
};

type typeAppleAuthApi = {
  init: (config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    usePopup: boolean;
  }) => void;
  signIn: () => Promise<typeAppleSignInResponse>;
};

declare global {
  // An `interface` is the only thing TypeScript merges into the built-in
  // `Window`, so the repo's type-over-interface rule cannot apply here.
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Window {
    AppleID?: { auth?: typeAppleAuthApi };
  }
}

export const APPLE_ID_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

/**
 * The two strings Apple uses when a person backed out.
 *
 * Both are treated as a cancellation, and the direction of that guess is
 * deliberate: showing an error modal to somebody who simply closed a window is
 * the exact failure R6 exists to stop, while swallowing a genuine error costs
 * one silent retry.
 */
export const APPLE_CANCEL_ERRORS = [
  "popup_closed_by_user",
  "user_cancelled_authorize",
] as const;

export type typeAppleSignInResult =
  | { kind: "idToken"; idToken: string }
  | { kind: "canceled" }
  | { kind: "failed" };

function readAppleErrorCode({ cause }: { cause: unknown }): string | null {
  if (typeof cause !== "object" || cause === null) {
    return null;
  }
  const { error } = cause as { error?: unknown };
  return typeof error === "string" ? error : null;
}

/**
 * Opens Apple's popup and collapses its whole error surface into three
 * outcomes.
 *
 * Note the asymmetry, and keep it. A script or API failure THROWS, because it
 * happens before the fan pressed anything and the caller must render no button.
 * A popup failure RETURNS `{ kind: "failed" }`, because the fan pressed and is
 * owed the error modal.
 */
export async function startAppleSignIn({
  webServicesId,
  redirectUri,
}: {
  /** `apple.webServicesId` from POST /oauth/provider-config-get. The Services ID, never the bundle id. */
  webServicesId: string;
  /** Must be an https origin registered as a Return URL on the Service ID. */
  redirectUri: string;
}): Promise<typeAppleSignInResult> {
  await loadExternalScript({ src: APPLE_ID_SCRIPT_SRC });

  const auth = window.AppleID?.auth;
  if (!auth) {
    throw new Error(
      "Sign in with Apple JS loaded but window.AppleID.auth is missing",
    );
  }

  auth.init({
    clientId: webServicesId,
    scope: "email",
    redirectURI: redirectUri,
    usePopup: true,
  });

  try {
    const response = await auth.signIn();
    const idToken = response.authorization?.id_token;
    if (typeof idToken === "string" && idToken.length > 0) {
      return { kind: "idToken", idToken };
    }
    return { kind: "failed" };
  } catch (cause: unknown) {
    const code = readAppleErrorCode({ cause });
    if (
      code !== null &&
      (APPLE_CANCEL_ERRORS as readonly string[]).includes(code)
    ) {
      return { kind: "canceled" };
    }
    return { kind: "failed" };
  }
}
